import React, { useState } from 'react';
import { X, Phone, Mail, Calendar, Clock, Euro } from 'lucide-react';
import { scrollToSection } from '../utils/navigation';

interface AtelierModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AtelierModal: React.FC<AtelierModalProps> = ({ isOpen, onClose }) => {
  const [selectedAtelier, setSelectedAtelier] = useState<string>('');

  if (!isOpen) return null;

  const ateliers = [
    {
      id: 'pizza',
      title: 'Atelier Pizza',
      duration: '3 heures',
      price: '90€',
      description: 'Apprenez les bases de la pizza napolitaine : préparation de la pâte, étalage à la main, garniture et cuisson.',
      includes: [
        'Préparation de la pâte traditionnelle',
        'Techniques d\'étalage à la main',
        'Choix et préparation des garnitures',
        'Cuisson au four',
        'Dégustation de vos créations'
      ]
    },
    {
      id: 'pizza-socca',
      title: 'Atelier Pizza & Socca',
      duration: '4 heures',
      price: '110€',
      description: 'Formation complète : pizza napolitaine + découverte de la Socca, spécialité niçoise traditionnelle.',
      includes: [
        'Tout le contenu de l\'atelier Pizza',
        'Préparation de la Socca niçoise',
        'Histoire et traditions locales',
        'Techniques de cuisson spécifiques',
        'Dégustation des deux spécialités'
      ]
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-2xl font-bold text-gray-900">Réserver un Atelier</h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <p className="text-gray-600">
            Choisissez l'atelier qui vous convient et découvrez l'art de la pizza napolitaine 
            avec Joseph Cardone dans une ambiance conviviale.
          </p>

          {/* Sélection des ateliers */}
          <div className="grid md:grid-cols-2 gap-6">
            {ateliers.map((atelier) => (
              <div 
                key={atelier.id}
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedAtelier === atelier.id 
                    ? 'border-red-500 bg-red-50' 
                    : 'border-gray-200 hover:border-red-300'
                }`}
                onClick={() => setSelectedAtelier(atelier.id)}
              >
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-bold text-gray-900">{atelier.title}</h4>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-red-600">{atelier.price}</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {atelier.duration}
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-4">{atelier.description}</p>
                
                <div className="space-y-2">
                  <h5 className="font-semibold text-gray-900">Inclus :</h5>
                  <ul className="space-y-1">
                    {atelier.includes.map((item, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-red-500 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Informations pratiques */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Informations pratiques</h4>
            <div className="grid md:grid-cols-2 gap-4 text-gray-600">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-red-600" />
                  <span>Lieu : Vallauris (06220)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-red-600" />
                  <span>Réservation : 06 60 47 67 40</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-red-600" />
                  <span>Email : ecoledelapizza@gmail.com</span>
                </div>
                <div className="text-sm text-gray-500">
                  Ateliers sur réservation uniquement
                </div>
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-4">
            <button 
              onClick={() => {
                if (selectedAtelier) {
                  onClose();
                  setTimeout(() => scrollToSection('contact'), 100);
                }
              }}
              disabled={!selectedAtelier}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold text-center transition-colors ${
                selectedAtelier 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {selectedAtelier ? 'Réserver maintenant' : 'Sélectionnez un atelier'}
            </button>
            <button 
              onClick={onClose}
              className="flex-1 border border-red-600 text-red-600 py-3 px-6 rounded-lg hover:bg-red-50 transition-colors font-semibold text-center"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtelierModal;